import { Box, FormLabel, Input, InputAdornment, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ColorField from 'app/app/editor/tabs/settings/style-editor/ColorField';
import FontField from 'app/app/editor/tabs/settings/style-editor/FontField';
import SizeField from 'app/app/editor/tabs/settings/style-editor/SizeField';
import styles from './StyleEditorContainer.styles';
import IndentPreview from 'app/app/editor/tabs/settings/style-editor/IndentPreview';
import FieldBySide from 'app/app/editor/tabs/settings/style-editor/FieldBySide';

import { IComponentNode } from 'app/app/editor/index.functions';
import React, { useState } from 'react';
import IconMi from 'components/common/icon/IconMi';
interface StylesEditorContainerProps {
    formState: any;
    setFormState: (value: any) => void;
    onChangeStyle: (value: any) => void;
    uid: string;
    node: IComponentNode;
}

const defaultPropertyList: any = {
    'flex-direction': {
        default: 'column',
    },
    'justify-content': {
        default: 'flex-start',
    },
    'align-items': {
        default: 'stretch',
    },
    'justify-self': {
        default: '',
    },
    'align-self': {
        default: '',
    },
    width: {
        default: '',
    },
    height: {
        default: '',
    },
    margin: {
        default: '',
    },
    padding: {
        default: '',
    },
    'background-color': {
        default: '',
    },
    'border-color': {
        default: '',
    },
    'border-radius': {
        default: '',
    },
    'border-width': {
        default: '',
    },
    resizable: {
        default: 'off',
    },
    'font-size': {
        default: '12px',
    },
    'font-family': {
        default: '',
    },
    color: {
        default: '',
    },
    'size-type-width': {
        default: 'fill',
    },
    'size-type-height': {
        default: 'fill',
    },
    'background-image': {
        default: '',
    },
    'background-size': {
        default: '',
    },
    'background-repeat': {
        default: '',
    },
};

export const getAppBuilderCurrentStyleValue = (formState: any, propertyName: string) => {
    return undefined === formState[propertyName]
        ? defaultPropertyList?.[propertyName]?.default
        : 'object' === typeof formState[propertyName]
          ? JSON.stringify(formState[propertyName])
          : formState[propertyName];
};

const StyleSection = ({
    alias,
    collapsedSection,
    toggleCollapseState,
    title,
    children,
}: {
    alias: string;
    collapsedSection: any;
    toggleCollapseState: (alias: string) => void;
    title: string;
    children: any;
}) => {
    return (
        <Stack sx={styles.section} className={collapsedSection?.[alias] ? 'section--collapsed' : 'section--expanded'}>
            <Stack
                direction={'row'}
                sx={styles.sectionTitle}
                onClick={() => {
                    toggleCollapseState(alias);
                }}
            >
                <Box sx={styles.sectionTitleLabel}>{title}</Box>
                <Box sx={styles.sectionTitleIcon}>
                    <IconMi icon={collapsedSection?.[alias] ? 'chevron-down' : 'chevron-up'} />
                </Box>
            </Stack>
            <Stack sx={styles.sectionControlsContainer} spacing={1}>
                {children}
            </Stack>
        </Stack>
    );
};
export default function StylesEditorContainer({
    formState,
    setFormState = () => {},
    onChangeStyle = () => {},
    uid,
    node,
}: StylesEditorContainerProps) {
    const isMenuContainer = node.subtype == 'page_menu';
    const [collapsedSection, setCollapsedSection] = useState<{ [key: string]: boolean }>({
        spacing: true,
        border: true,
        background: true,
        text: true,
    });
    const getTextField = (propertyName: string, placeholderValue?: string, endAdornment?: any) => {
        const tmpValue = getAppBuilderCurrentStyleValue(formState, propertyName);
        let value = tmpValue;

        switch (propertyName) {
            case 'border-width':
                value = tmpValue.replace('px', '');
                break;
            case 'background-image':
                value = tmpValue.replace(/^url\((['"]?)(.*?)\1\)$/, '$2');
                break;
        }

        return (
            <Input
                className={'design-variant--secondary'}
                fullWidth
                value={value}
                onChange={(e) => {
                    if (propertyName === 'border-width') {
                        onChangeStyle({
                            [propertyName]: isNaN(+e.target.value) ? e.target.value : `${e.target.value}px`,
                        });
                    } else if (propertyName === 'background-image') {
                        onChangeStyle({ [propertyName]: `url(${e.target.value})` });
                    } else {
                        onChangeStyle({ [propertyName]: e.target.value });
                    }
                }}
                placeholder={placeholderValue ?? ''}
                inputProps={{ 'data-1p-ignore': true }}
                endAdornment={endAdornment}
            />
        );
    };

    const toggleCollapseState = (sectionName: string) => {
        setCollapsedSection({ ...collapsedSection, [sectionName]: !collapsedSection?.[sectionName] });
    };

    return (
        <Stack>
            <StyleSection
                alias={'size'}
                title={'Size'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Width
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <SizeField
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            propertyName={'width'}
                            uid={uid}
                        />
                    </Box>
                </Box>
                {!isMenuContainer && (
                    <Box sx={styles.propertyContainer}>
                        <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                            Height
                        </FormLabel>
                        <Box sx={styles.propertyControlHolder}>
                            <SizeField
                                formState={formState}
                                onChangeStyle={onChangeStyle}
                                propertyName={'height'}
                                uid={uid}
                            />
                        </Box>
                    </Box>
                )}
            </StyleSection>

            <StyleSection
                alias={'layout'}
                title={'Layout'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                {isMenuContainer ? (
                    <>
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Dockable
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={
                                        getAppBuilderCurrentStyleValue(formState, 'left') == 'auto' ? 'right' : 'left'
                                    }
                                    exclusive
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onChangeStyle({
                                                left: value == 'left' ? 0 : 'auto',
                                                right: value == 'left' ? 'auto' : 0,
                                            });
                                        }
                                    }}
                                >
                                    <ToggleButton value="left">Left</ToggleButton>
                                    <ToggleButton value="right">Right</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>

                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Collapse with
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={getAppBuilderCurrentStyleValue(formState, 'menu-collapse-type') ?? 'x'}
                                    exclusive
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onChangeStyle({ 'menu-collapse-type': value });
                                        }
                                    }}
                                >
                                    <ToggleButton value="x">
                                        <IconMi icon={'times'} />
                                    </ToggleButton>
                                    <ToggleButton value="arrow">
                                        <IconMi icon={'chevrons-left'} />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box sx={styles.propertyContainer}>
                        <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                            Direction
                        </FormLabel>
                        <Box sx={styles.propertyControlHolder}>
                            <ToggleButtonGroup
                                value={getAppBuilderCurrentStyleValue(formState, 'flex-direction')}
                                exclusive
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onChangeStyle({ 'flex-direction': value });
                                    }
                                }}
                                sx={{
                                    svg: { width: '16px', height: '16px' },
                                }}
                                fullWidth
                                className={'design-variant--secondary'}
                            >
                                <ToggleButton value="row">
                                    <IconMi icon={'arrows-alt-h'} />
                                    <Box sx={{ ml: 1 }}>Horizontal</Box>
                                </ToggleButton>
                                <ToggleButton value="column">
                                    <IconMi icon={'arrows-alt-v'} />
                                    <Box sx={{ ml: 1 }}>Vertical</Box>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                )}
                {node.type == 'container' && (
                    <>
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Align children by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'X' : 'Y'}
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={getAppBuilderCurrentStyleValue(formState, 'justify-content')}
                                    exclusive
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onChangeStyle({ 'justify-content': value });
                                        }
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-left'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-left'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-center'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-center'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-right'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-right'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-around">
                                        {' '}
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-space-around'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-space-around'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-between">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-space-between'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-space-between'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Align children by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'Y' : 'X'}
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={getAppBuilderCurrentStyleValue(formState, 'align-items')}
                                    exclusive
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onChangeStyle({ 'align-items': value });
                                        }
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-left'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-left'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-center'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-center'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-right'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-right'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="stretch">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-stretch'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-stretch'} />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </>
                )}
                {!isMenuContainer && (
                    <>
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Align self by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'X' : 'Y'}
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={getAppBuilderCurrentStyleValue(formState, 'justify-self')}
                                    exclusive
                                    onChange={(event, value) => {
                                        onChangeStyle({ 'justify-self': value ?? '' });
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-left'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-left'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-center'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-center'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-right'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-right'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-around">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-space-around'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-space-around'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-between">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi icon={'builder-alignment-space-between'} />
                                        ) : (
                                            <IconMi
                                                icon={'builder-alignment-space-between'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Align self by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'Y' : 'X'}
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={getAppBuilderCurrentStyleValue(formState, 'align-self')}
                                    exclusive
                                    onChange={(event, value) => {
                                        onChangeStyle({ 'align-self': value ?? '' });
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-left'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-left'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-center'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-center'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-right'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-right'} />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="stretch">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <IconMi
                                                icon={'builder-alignment-stretch'}
                                                sx={{ transform: 'rotate(90deg)' }}
                                            />
                                        ) : (
                                            <IconMi icon={'builder-alignment-stretch'} />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </>
                )}
            </StyleSection>

            <StyleSection
                alias={'spacing'}
                title={'Spacing'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '264px', maxWidth: '90%' }}>
                        <IndentPreview formState={formState} uid={uid} onChangeStyle={onChangeStyle} />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Margin
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'side'}
                            propertyName={'margin'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Padding
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'side'}
                            propertyName={'padding'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'border'}
                title={'Border'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Border Width
                    </FormLabel>
                    <Stack direction={'row'} spacing={1}>
                        <Box sx={{ ...styles.propertyControlHolder, width: '100%' }}>
                            {getTextField(
                                'border-width',
                                '0',
                                <InputAdornment position="end" sx={{ mx: 0 }}>
                                    <Box sx={{ px: 1 }}>px</Box>
                                </InputAdornment>,
                            )}
                        </Box>
                    </Stack>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Border Radius
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'corner'}
                            propertyName={'border-radius'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Border Color
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'border-color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'background'}
                title={'Background'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Background Color
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'background-color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Background URL
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-image')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Background Size
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-size')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Background Repeat
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-repeat')}</Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'text'}
                title={'Text'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Font
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <FontField propertyName={'font-family'} onChangeStyle={onChangeStyle} formState={formState} />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Font Size
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>{getTextField('font-size')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                        Color
                    </FormLabel>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            {/*
            <StyleSection
                alias={'spacing'}
                title={'Spacing'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
            <Box>Section content</Box>
            </StyleSection>
            */}
        </Stack>
    );
}
